import { TFunction } from 'i18next';

export const allowedPasswordCharactersString = '~@#%^*_+=-?:{}[]\\/';
export const allPasswordCharacters = `$&+,:;=?@#|'<>.^*()%!-`;

const escapeForRegex = (str: string): string => str.replace(/[-\\[\]^]/g, '\\$&');

export const requirements = [
  {
    re: /^.{8,}$/,
    label: 'Содержит не менее 8 символов'
  },
  {
    re: /^[a-zA-Z0-9$&+,:;=?@#|'<>.^*()%!/-]*$/,
    label: 'Содержит только латинские символы'
  },
  { re: /[0-9]/, label: 'добавьте число' },
  { re: /[a-z]/, label: 'добавьте строчную букву' },
  { re: /[A-Z]/, label: 'добавьте заглавную букву' },
  { re: /[$&+,:;=?@#|'<>.^*()%!-]/, label: 'добавьте специальные символы (! “ # $ % ‘ () *)' }
];

export const getPasswordStrength = (
  strength: number,
  t: TFunction
): { color: string; strengthString: string } => {
  if (strength < 50) {
    return { color: '#FF6B6B', strengthString: t('passwordReset.reliability.unreliable') };
  } else if (strength >= 50 && strength < 100) {
    return { color: '#FCC419', strengthString: t('passwordReset.reliability.quite_reliable') };
  } else if (strength === 100) {
    return { color: '#51CF66', strengthString: t('passwordReset.reliability.reliable') };
  } else {
    return { color: '', strengthString: '' };
  }
};

export const fullRegExRequirements = new RegExp(
  requirements.map(({ re }) => `(${re.source})`).join('|')
);

export const generatePassword = (regex: RegExp, minLength?: number): string => {
  const characters = `ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789${allPasswordCharacters}`;

  let password = '';
  const minimumLength = minLength || 8;
  while (!regex.test(password)) {
    password = '';
    for (let i = 0; i <= minimumLength; i++) {
      password += characters.charAt(Math.floor(Math.random() * characters.length));
    }
  }

  return password;
};

interface IGeneratePasswordRegExp {
  options?: Partial<{
    UseLowLetter: boolean;
    UseCapitalLetters: boolean;
    UseDigital: boolean;
    UseSpecialCharacters: boolean;
    UseAllowedSpecialCharacters: boolean;
    MinLength: number;
  }>;
  maxLengthPassword?: number;
}

export function generatePasswordRegExp({
  options = {},
  maxLengthPassword = 30
}: IGeneratePasswordRegExp): RegExp {
  const characterClasses: string[] = [];

  // ❌ Пробелы запрещены
  characterClasses.push('(?!.*\\s)');

  // ✅ Проверки на наличие нужных символов
  if (options.UseLowLetter) {
    characterClasses.push('(?=.*[a-z])');
  }

  if (options.UseCapitalLetters) {
    characterClasses.push('(?=.*[A-Z])');
  }

  if (options.UseDigital) {
    characterClasses.push('(?=.*[0-9])');
  }

  // Подготовка списка допустимых символов
  let allowedCharacters = '';

  if (options.UseSpecialCharacters) {
    if (options.UseAllowedSpecialCharacters) {
      const escapedAllowed = escapeForRegex(allowedPasswordCharactersString);
      characterClasses.push(`(?=.*[${escapedAllowed}])`);
      allowedCharacters = `a-zA-Z0-9${escapedAllowed}`;
    } else {
      // ✅ Проверяем наличие хотя бы одного НЕ a-zA-Z0-9 символа (any special char)
      characterClasses.push(`(?=.*[^a-zA-Z0-9])`);
      allowedCharacters = `\\x21-\\x7E`; // printable ASCII (кроме пробела)
    }
  } else {
    allowedCharacters = 'a-zA-Z0-9';
  }

  const minLength =
    options.MinLength && options.MinLength >= 8 && options.MinLength <= 30 ? options.MinLength : 8;

  // Полный паттерн
  const pattern = `^${characterClasses.join(
    ''
  )}(?!.*[^${allowedCharacters}])[${allowedCharacters}]{${minLength},${maxLengthPassword}}$`;

  return new RegExp(pattern);
}
